import {
    IServerMessage,
    IServerSentEventsConsumer,
    ServerSentEventError,
    ServerSentEventErrorTypes,
} from '../../../infra';
import {
    ExtendedRunStatus,
    ExtendedRunStatusType,
    IConversationRun,
} from '../model/ConversationRun';
import { IPlacerAssistantFactory } from '../model/PlacerAssistantFactory';
import { ContainerDTO, RunDTO } from './AssistantDTOs';
import { IPlacerAssistantService } from './PlacerAssistantService';
import { getCurrentTimestamp, wait, type IEventBroker } from '@zdrbm/zdr-native-tools';
import { PiError } from 'features/pi/model/pi-errors/PiError';

type ReoMessageCallback = Parameters<IEventBroker<IServerMessage>['reo']>[0];
export interface IRunLifecycleManager {
    start(): Promise<void>;
}

export const DEFAULT_MAX_STREAM_RETRIES = 3;
export const DEFAULT_POLLING_INTERVAL = 3000;
export const DEFAULT_MAX_POLLING_TIME = 2 * 60 * 1000; // 2 min;
export const DEFAULT_WAIT_TIME_BETWEEN_RETRIES = 2000; // 2 sec;
export interface IRunLifecycleManagerOptions {
    maxStreamRetries?: number;
    pollingInterval?: number;
    maxPollingTime?: number;
    waitBetweenRetries?: number;
}

export class RunLifecycleManager implements IRunLifecycleManager {
    private interval: NodeJS.Timeout | undefined = undefined;
    private retries: number = 0;
    private readonly maxRetries: number;
    private readonly pollingInterval: number;
    private readonly maxPollingTime: number;
    private readonly waitBetweenRetries: number;

    constructor(
        private run: IConversationRun,
        private placerAssistantFactory: IPlacerAssistantFactory,
        private messageConsumer: IServerSentEventsConsumer,
        private assistantService: IPlacerAssistantService,
        private options?: IRunLifecycleManagerOptions,
    ) {
        this.maxRetries = this.options?.maxStreamRetries ?? DEFAULT_MAX_STREAM_RETRIES;
        this.pollingInterval = this.options?.pollingInterval ?? DEFAULT_POLLING_INTERVAL;
        this.maxPollingTime = this.options?.maxPollingTime ?? DEFAULT_MAX_POLLING_TIME;
        this.waitBetweenRetries =
            this.options?.waitBetweenRetries ?? DEFAULT_WAIT_TIME_BETWEEN_RETRIES;
    }

    private stopPolling(): void {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = undefined;
        }
    }

    private reportError(prefix: string, originalError: unknown): void {
        console.error(new PiError(`RunLifecycleManager::${prefix}`), originalError, {
            runId: this.run.getId(),
            conversationId: this.run.conversationId.get(),
            input: this.run.input.get(),
            status: this.run.status.get(),
        });
    }

    updateRunUsingPolling = async () => {
        const startTime = getCurrentTimestamp();
        this.stopPolling();
        this.interval = setInterval(async () => {
            const currentTime = getCurrentTimestamp();

            try {
                if (currentTime - startTime > this.maxPollingTime) {
                    throw new PiError('Polling time exceeded');
                }
                const run: IConversationRun = await this.assistantService.getRunById(
                    this.run.conversationId.get(),
                    this.run.getId(),
                );

                this.run.updateFrom(run);
                if (run.hasReachedTerminalStatus()) {
                    this.stopPolling();
                }
            } catch (error) {
                this.reportError('updateRunUsingPolling - error', error);
                this.run.changeStatus(ExtendedRunStatus.CLIENT_ERROR);
                this.stopPolling();
            }
        }, this.pollingInterval);
    };

    handleMessage: ReoMessageCallback = (message) => {
        try {
            const data = message.getJsonData<Record<string, unknown>>();
            data.forEach((updateItem) => {
                if (updateItem.status) {
                    const newStatus = updateItem.status as ExtendedRunStatusType;
                    this.run.changeStatus(newStatus);
                } else if (updateItem.container) {
                    const isPartial =
                        !!(updateItem.container as any).is_partial || !!updateItem.is_partial;
                    const containerDTO = updateItem.container as ContainerDTO;
                    const containerId = containerDTO.id;

                    const container = this.placerAssistantFactory.createConversationRunContainer(
                        updateItem.container as ContainerDTO,
                        containerId,
                    );

                    const existingContainer = this.run.responseContainers.getItem(
                        container.getId(),
                    );

                    if (existingContainer && isPartial) {
                        existingContainer.appendFromContainer(container);
                    } else if (existingContainer) {
                        existingContainer.updateFrom(container);
                    } else {
                        this.run.addContainer(container);
                    }
                } else if (updateItem.run) {
                    const run = this.placerAssistantFactory.createConversationRun(
                        updateItem.run as RunDTO,
                    );
                    this.run.updateFrom(run);
                }
            });
        } catch (error) {
            this.reportError('handleMessage - error', error);
            this.messageConsumer.abort();
        }
    };

    private shouldRetry(error: unknown): boolean {
        let shouldTryAgain = false;
        if (
            ServerSentEventError.isServerSentEventError(error) &&
            this.retries < this.maxRetries - 1
        ) {
            shouldTryAgain = error.errorType === ServerSentEventErrorTypes.CONNECTION_ERROR;
        }

        return shouldTryAgain;
    }

    private async updateRunUsingServerSentEvents(): Promise<void> {
        try {
            this.messageConsumer.onMessage.reo(this.handleMessage);
            this.messageConsumer.onDone.reo(this.updateRunUsingPolling);
            await this.messageConsumer.listen();
        } catch (error) {
            if (this.shouldRetry(error)) {
                this.retries++;
                await wait(this.waitBetweenRetries); // Give the connection a chance to reconnect
                await this.updateRunUsingServerSentEvents();
            } else {
                this.reportError('updateRunUsingServerSentEvents - error', error);

                throw error;
            }
        }
    }

    async start(): Promise<void> {
        try {
            await this.updateRunUsingServerSentEvents();
        } catch (error) {
            await this.updateRunUsingPolling();
        }
    }
}
